import m from "mithril";
import Requester from "../../shared/request";
import Gateway from "../../shared/app_settings";
import Toasts from "../../shared/toasts";
import ModalManager from "../../shared/modal";
import i18n from "../../shared/i18n/i18n";
import modal_text_view from "../../shared/components/modal_text_view";
import sanitizeHtml from "sanitize-html";

const Component_Unit_Actions_Assessment = {
    loading: false,
    assessmentReSubmitted: false,
    feedbackResubmit: false,

    // Reload callback from parent
    reloadCallback: null,

    openModalWithInstructions(title, instructions, callback, btnText) {
        ModalManager.open(title, [], modal_text_view, {
            callback,
            text: [m("div.richtext", m.trust(sanitizeHtml(instructions || "", {allowedTags: sanitizeHtml.defaults.allowedTags, allowedAttributes: {"*": ["style", "class"], "a": ["href", "target"]}}))), m("br"), m("br"), m("br")],
            btnText,
        });
    },

    openFeedbackModal(unit, course) {
        ModalManager.open(unit.name, [], modal_text_view, {
            callback: () => {
                this.unitSubmitForAssessment(unit, course);
                this.assessmentReSubmitted = true;
            },
            text: [
                m("div", [
                    m("span", { style: "font-weight: bold;" }, i18n.t("assessor_name") + ": "),
                    m("span", unit.enrolment.assessorName || "-")
                ]),
                m("br"),
                m("span", { style: "font-weight: bold;" }, i18n.t("assessor_comment") + ": "),
                m("br"),
                unit.enrolment.assessorFeedback,
                m("br"),
                m("br"),
                m("br"),
            ],
            btnText: i18n.t("reSubmit_assessment"),
        });
    },

    // Helper functions to determine enrolment status
    isReturnForAssessment(e) {
        return !e.isPassed && !this.assessmentReSubmitted && e.assessmentSubmitted && e.assessmentUnitReturned;
    },

    isAdminTriggeredReturn(e) {
        return !e.assessmentSubmitted && e.assessmentUnitReturned;
    },

    isFeedbackAvailable(e) {
        return e.assessorFeedback && e.isCompleted && e.isPassed;
    },

    isReadyToAssess(e) {
        return !e.isCompleted && !e.assessmentSubmitted && !e.assessmentUnitReturned && !this.assessmentReSubmitted;
    },

    isAwaitingAssessment(e) {
        return e.assessmentSubmitted && !e.isPassed && !e.assessmentUnitReturned;
    },

    async getUnitInstructions(unitId) {
        if (this.loading) return null;
        this.loading = true;
        try {
            return await Requester.get(`${Gateway.admin}units/${unitId}`);
        } catch (err) {
            Toasts.add("error", i18n.t("error"), i18n.t(err.response?.properties?.errorCode));
            return null;
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    viewAssessmentUnitInstructions(unit, course, instructions) {
        this.openModalWithInstructions(unit.name, instructions);
    },

    unitSubmitForAssessment(unit, course) {
        // Stub function: implement actual submission logic here
        Toasts.add("success", i18n.t("assessment_submitted"));
        if (typeof this.reloadCallback === "function") {
            this.reloadCallback();
        }
    },

    oninit({ attrs }) {
        this.reloadCallback = attrs.reloadCallback;
    },

    view({ attrs }) {
        const { unit, course } = attrs;
        const enrolment = unit?.enrolment;

        if (!enrolment?.allowAccess) return null;

        const buttons = [];

        const loadAndOpenInstructions = async () => {
            const res = await this.getUnitInstructions(unit.id);
            if (res?.properties?.instructions) {
                this.openModalWithInstructions(
                    unit.name,
                    res.properties.instructions,
                    () => {
                        this.viewAssessmentUnitInstructions(unit, course, res.properties.instructions);
                        this.assessmentReSubmitted = true;
                    },
                    i18n.t("close")
                );
            }
        };

        if (this.isReadyToAssess(enrolment)) {
            buttons.push(
                m(
                    "button.btn-cta",
                    {
                        onclick: () => {
                            this.feedbackResubmit = false;
                            loadAndOpenInstructions();
                        },
                        disabled: this.loading,
                    },
                    i18n.t("view_details")
                )
            );
        }

        if (this.isAwaitingAssessment(enrolment)) {
            buttons.push(
                m(
                    "button.with-icon",
                    {
                        onclick: () => {
                            this.feedbackResubmit = false;
                            loadAndOpenInstructions();
                        },
                        disabled: this.loading,
                    },
                    [i18n.t("view_details"), m("i.icon-check.ml-1.pr-0")]
                )
            );
        }

        if (this.isReturnForAssessment(enrolment) || this.isAdminTriggeredReturn(enrolment)) {
            buttons.push(
                m(
                    "button.btn-cta",
                    {
                        onclick: () => {
                            this.feedbackResubmit = true;
                            this.openFeedbackModal(unit, course);
                        },
                        class: !enrolment.isCompleted ? "ml-2" : "",
                        disabled: this.loading,
                    },
                    i18n.t("return_for_assessment")
                )
            );
        }

        if (this.isFeedbackAvailable(enrolment)) {
            buttons.push(
                m(
                    "button.btn-cta",
                    {
                        onclick: () => {
                            this.feedbackResubmit = false;
                            this.openFeedbackModal(unit, course);
                        },
                        class: !enrolment.isCompleted ? "ml-2" : "",
                        disabled: this.loading,
                    },
                    i18n.t("view_feedback")
                )
            );
        }

        return buttons;
    },
};

export default Component_Unit_Actions_Assessment;
